import {useCallback, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import uniq from 'lodash.uniq';
import {MessageContext} from 'src/contexts/message-context';
import {getError} from 'src/utils/get-error';

import {rangeSelect} from '@pb/kit-component-selection';


import {
    clearSelection as clearSelectionAction,
    selectDataTableAllIds,
    selectDataTableLastCheckedIndex,

    selectDataTableSelection,
    setLastCheckedIndex as setLastCheckedIndexAction,
    setSearchedIds as setSearchedIdsAction,
    setSelection as setSelectionAction,
} from '../services/data-table-selection-slice';

import type {RangeShift, SelectionType} from '@pb/kit-component-selection';
import type {Error} from 'src/openapi/admin/models/Error';
import type {AppState} from 'src/store/typedef';

type Props = {
    rows: Array<number | string>,
    key: string,
    onSetSelection?: (ids: Array<number | string>) => void,
    getEntityIds?: () => Promise<{
        type: string,
        payload: {
            data: number[] | string[],
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
};

export const useDataTableSelection = ({rows, key, onSetSelection, getEntityIds}: Props) => {
    const dispatch = useDispatch();
    const {setErrorMessage} = useContext(MessageContext);

    const selectAllIds = useCallback((state: AppState) => selectDataTableAllIds(state, key), [key]);
    const selectSelection = useCallback((state: AppState) => selectDataTableSelection(state, key), [key]);
    const selectLastCheckedIndex = useCallback((state: AppState) => selectDataTableLastCheckedIndex(state, key), [key]);

    const allIds = useSelector(selectAllIds);
    const selection = useSelector(selectSelection);
    const lastCheckedIndex = useSelector(selectLastCheckedIndex);

    const setLastCheckedIndex = useCallback((index: number) => {
        if (lastCheckedIndex !== index) {
            dispatch(setLastCheckedIndexAction({key, lastCheckedIndex: index}));
        }
    }, [key, lastCheckedIndex, dispatch]);

    const setSelection = useCallback(async (selectionParams: SelectionType) => {
        if (onSetSelection) {
            onSetSelection(selectionParams.ids);
        }

        if (getEntityIds && selectionParams.inverted) {
            const loadedIdsResponse = await getEntityIds();

            if (loadedIdsResponse.payload.success) {
                dispatch(setSearchedIdsAction({key, allIds: loadedIdsResponse.payload.data}));
            } else {
                setErrorMessage(getError({error: loadedIdsResponse.payload.data, separator: ' '}));
            }
        }

        dispatch(setSelectionAction({key, selection: selectionParams}));
    }, [key, getEntityIds, onSetSelection, dispatch, setErrorMessage]);

    const clearSelection = useCallback(() => {
        dispatch(clearSelectionAction(key));
    }, [key, dispatch]);

    const rangeShift: RangeShift = useCallback(({rowIndex, ids, checked, inverted}) => {
        const data = (rows || []).map(entityId => ({entityId}));
        if (lastCheckedIndex === undefined || !data) {
            return null;
        }
        return rangeSelect({rowIndex, ids, checked, inverted, lastCheckedIndex, data});
    }, [rows, lastCheckedIndex]);

    const setSelectionAll = useCallback(async () => {
        dispatch(setSelectionAction({key, selection: {inverted: true, ids: []}}));
        if (getEntityIds) {
            const loadedIdsResponse = await getEntityIds();

            if (loadedIdsResponse.payload.success) {
                dispatch(setSearchedIdsAction({key, allIds: loadedIdsResponse.payload.data}));
            } else {
                setErrorMessage(getError({error: loadedIdsResponse.payload.data, separator: ' '}));
            }
        }
    }, [key, getEntityIds, dispatch, setErrorMessage]);

    const setSelectionPage = useCallback(({inverted}: SelectionType) => {
        if (!inverted) {
            if (onSetSelection) {
                onSetSelection(uniq([...selection.ids, ...rows]));
            }
            dispatch(setSelectionAction({
                key,
                selection: {inverted, ids: uniq([...selection.ids, ...rows])},
            }));
        } else {
            if (onSetSelection) {
                onSetSelection(uniq(selection.ids.filter(id => !rows.includes(id))));
            }
            dispatch(setSelectionAction({
                key,
                selection: {inverted, ids: selection.ids.filter(id => !rows.includes(id))},
            }));
        }
    }, [key, rows, selection.ids, onSetSelection, dispatch]);

    const deselectAll = useCallback(() => {
        if (onSetSelection) {
            onSetSelection([]);
        }
        dispatch(setSelectionAction({key, selection: {inverted: false, ids: []}}));
    }, [key, onSetSelection, dispatch]);

    return {
        allIds,
        selection,
        rows,
        setSelectionAll,
        setSelectionPage,
        deselectAll,
        setSelection,
        clearSelection,
        setLastCheckedIndex,
        rangeShift,
    };
};
